import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangleIcon, ArrowLeftIcon, CheckCircle2Icon, SaveIcon } from 'lucide-react';
import { useData } from '../../store/DataContext';
import { Button } from '../../components/ui/Button';
import { ConsultationTypeBadge } from '../../components/ui/ConsultationTypeBadge';
import { Encounter, Patient } from '../../types';
import { AmendableNote, amendableFields, diffNotes, readField, writeField } from '../../utils/fileAmendment';
import { AmendFileRecordSummary } from './AmendFileRecordSummary';
import { AmendmentHistory } from './AmendmentHistory';
type AmendFileEditorProps = {
  patient: Patient;
  encounter: Encounter;
  onBack: () => void;
};
const toAmendable = (encounter: Encounter): AmendableNote => {
  const {
    editHistory: _history,
    addenda: _addenda,
    ...note
  } = encounter.clinicalNote!;
  return note;
};
const formatDateTime = (date: Date) => date.toLocaleString('en-GB', {
  day: '2-digit',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit'
});
const groups = Array.from(new Set(amendableFields.map(f => f.group)));
export function AmendFileEditor({
  patient,
  encounter,
  onBack
}: AmendFileEditorProps) {
  const {
    currentUser,
    amendPatientFile
  } = useData();
  // Baseline = the note as currently saved; re-based after each successful save.
  const [baseline, setBaseline] = useState<AmendableNote>(() => toAmendable(encounter));
  const [draft, setDraft] = useState<AmendableNote>(baseline);
  const [addendum, setAddendum] = useState('');
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const changes = useMemo(() => diffNotes(baseline, draft), [baseline, draft]);
  const changedPaths = useMemo(() => new Set(changes.map(c => c.field)), [changes]);
  const hasEdits = changes.length > 0 || addendum.trim().length > 0;
  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaveError('');
    setSuccess('');
    if (!reason.trim()) {
      setReasonError('Please provide the reason for this amendment.');
      document.getElementById('amend-reason')?.focus();
      return;
    }
    setReasonError('');
    if (!hasEdits) {
      setSaveError('No changes yet. Edit a field or add a supplementary note.');
      return;
    }
    setIsSaving(true);
    const result = await amendPatientFile({
      encounterId: encounter.id,
      amendedNote: draft,
      changes,
      addendum: addendum.trim() || undefined,
      reason: reason.trim()
    });
    setIsSaving(false);
    if (!result.ok) {
      // Keep everything the clinician typed so they can retry.
      setSaveError(`${result.error || 'The amendment could not be saved.'} Your changes are still on this page — try again.`);
      return;
    }
    setBaseline(draft);
    setAddendum('');
    setReason('');
    setSuccess(`Amendment saved to file ${patient.bmcFileNumber} on ${formatDateTime(new Date(result.amendedAt!))}.`);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const inputClass = 'block w-full rounded-md border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';
  return <form onSubmit={handleSave} className="space-y-5" noValidate data-id="element-5755" data-mp="1qhhl1po96kay">
      <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-5756" data-mp="1ljph1xac37qe">
        <div data-id="element-5757" data-mp="1lhd4jznchpkq">
          <button type="button" onClick={onBack} className="mb-2 inline-flex items-center gap-1 text-sm font-medium text-blue-700 hover:underline" data-id="element-5758" data-mp="3s9xeig02mijr">
            <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-5759" data-mp="3pphimf6uqauv" /> Back to search
          </button>
          <p className="font-mono text-sm font-semibold text-blue-700" data-id="element-5760" data-mp="3tr2qtn8fy0yq">
            {patient.bmcFileNumber}
          </p>
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-5761" data-mp="2k9t4tcuwjyy">
            Amend file — {patient.firstName} {patient.lastName}
          </h2>
          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-sm text-gray-600" data-id="element-5762" data-mp="7jcn9irat5s5">
            <span data-id="element-5763" data-mp="362tcf6dk4fhm">
              Visit {new Date(encounter.startedAt).toLocaleDateString('en-GB')}
            </span>
            <span aria-hidden="true" data-id="element-5764" data-mp="3b0uv3q72mcsk">·</span>
            <span data-id="element-5765" data-mp="3fz609nmf7z4g">
              Original clinician: {baseline.clinicianName || 'Not recorded'}
            </span>
            <ConsultationTypeBadge type={encounter.consultationType} size="sm" data-id="element-5766" data-mp="3krfzxvdc8djx" />
          </div>
        </div>
      </div>

      {success && <p role="status" className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800" data-id="element-5767" data-mp="2n8wd6nd49j6g">
          <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-5768" data-mp="2kq2h5low6rzd" />
          {success}
        </p>}

      <AmendFileRecordSummary patient={patient} encounter={encounter} data-id="element-5769" data-mp="2fpepcxsua743" />

      <section aria-labelledby="amend-notes-heading" className="rounded-xl border border-gray-200 bg-white" data-id="element-5770" data-mp="2aqtpizhi1pg6">
        <header className="border-b border-gray-100 px-5 py-3" data-id="element-5771" data-mp="25vo401s0ecsm">
          <h3 id="amend-notes-heading" className="font-semibold text-gray-900" data-id="element-5772" data-mp="21t9mpm3znl3g">
            Clinical notes
          </h3>
          <p className="text-xs text-gray-500" data-id="element-5773" data-mp="1wyx2vaylo7r6">
            Correct any field below. The original wording is kept in the
            amendment history. Lists take one item per line.
          </p>
        </header>
        <div className="space-y-6 px-5 py-4" data-id="element-5774" data-mp="1n0zemngqqayk">
          {groups.map(group => <fieldset key={group} data-id="element-5775" data-mp="1i5tthqv5x0bj">
              <legend className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500" data-id="element-5776" data-mp="1e2dw2sb9qvxs">
                {group}
              </legend>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-id="element-5777" data-mp="13fh4d0yl6kvp">
                {amendableFields.filter(field => field.group === group).map(field => {
              const id = `amend-${field.path.replace('.', '-')}`;
              const changed = changedPaths.has(field.path);
              const value = readField(draft, field);
              const wide = field.kind !== 'text';
              return <div key={field.path} className={wide ? 'md:col-span-2' : ''} data-id="element-5778" data-mp="1r5be39nlztbq">
                        <div className="mb-1 flex items-center gap-2" data-id="element-5779" data-mp="1dfzqk2dmidgs">
                          <label htmlFor={id} className="text-sm font-medium text-gray-800" data-id="element-5780" data-mp="1ie1bksum0muw">
                            {field.label}
                          </label>
                          {changed && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800" data-id="element-5781" data-mp="2av3zeavwq4uf">
                              Corrected
                            </span>}
                        </div>
                        {field.kind === 'text' ? <input id={id} type="text" value={value} onChange={e => setDraft(d => writeField(d, field, e.target.value))} className={`${inputClass} ${changed ? 'border-amber-400 bg-amber-50/40' : 'border-gray-300'}`} data-id="element-5782" data-mp="2tzgwiktg5ij9" /> : <textarea id={id} rows={field.kind === 'list' ? 3 : 3} value={value} onChange={e => setDraft(d => writeField(d, field, e.target.value))} className={`${inputClass} ${changed ? 'border-amber-400 bg-amber-50/40' : 'border-gray-300'}`} data-id="element-5783" data-mp="2693bvfd6ylyp" />}
                        {changed && <p className="mt-1 whitespace-pre-line text-xs text-gray-500" data-id="element-5784" data-mp="1oy7hwz6gjbu8">
                            <span className="font-medium" data-id="element-5785" data-mp="1tsbqfmvdfrxf">Original:</span>{' '}
                            {readField(baseline, field) || '(blank)'}
                          </p>}
                      </div>;
            })}
              </div>
            </fieldset>)}

          <div data-id="element-5786" data-mp="27oz2kwvwaonx">
            <label htmlFor="amend-addendum" className="mb-1 block text-sm font-medium text-gray-800" data-id="element-5787" data-mp="2cpmnr1xg0ba8">
              Supplementary note{' '}
              <span className="font-normal text-gray-500" data-id="element-5788" data-mp="de2c7lkufjkb">
                (added alongside the original notes — nothing is overwritten)
              </span>
            </label>
            <textarea id="amend-addendum" rows={4} value={addendum} onChange={e => setAddendum(e.target.value)} placeholder="Additional findings, clarifications, treatment details or information obtained after the consultation…" className={`${inputClass} border-gray-300`} data-id="element-5789" data-mp="asm7ui1wm8ty" />
          </div>
        </div>
      </section>

      {encounter.clinicalNote && <AmendmentHistory note={encounter.clinicalNote} data-id="element-5790" data-mp="tqfadj8m8gqt" />}

      <section aria-labelledby="amend-details-heading" className="rounded-xl border-2 border-blue-200 bg-blue-50/40" data-id="element-5791" data-mp="oru8lfdptp09">
        <h3 id="amend-details-heading" className="border-b border-blue-100 px-5 py-3 font-semibold text-gray-900" data-id="element-5792" data-mp="jv3wf3517dk4">
          Amendment Details
        </h3>
        <div className="grid grid-cols-1 gap-4 px-5 py-4 md:grid-cols-2" data-id="element-5793" data-mp="34ji3z6d2z9gy">
          <div data-id="element-5794" data-mp="30hwkv39ydyyb">
            <label htmlFor="amend-by" className="mb-1 block text-sm font-medium text-gray-800" data-id="element-5795" data-mp="30heswiubq6w7">
              Amended By
            </label>
            <input id="amend-by" type="text" readOnly value={currentUser ? `${currentUser.fullName} (ID: ${currentUser.id})` : ''} className={`${inputClass} cursor-not-allowed border-gray-200 bg-gray-100 text-gray-700`} data-id="element-5796" data-mp="1rr75zsmnadrk" />
          </div>
          <div data-id="element-5797" data-mp="1wo7cg4nepij7">
            <label htmlFor="amend-at" className="mb-1 block text-sm font-medium text-gray-800" data-id="element-5798" data-mp="2beodhmw4q6fk">
              Date and Time of Amendment
            </label>
            <input id="amend-at" type="text" readOnly value={formatDateTime(now)} aria-describedby="amend-at-hint" className={`${inputClass} cursor-not-allowed border-gray-200 bg-gray-100 text-gray-700`} data-id="element-5799" data-mp="26fk691srmirv" />
            <p id="amend-at-hint" className="mt-1 text-xs text-gray-500" data-id="element-5800" data-mp="q1u1rtxqhshp">
              The exact time is stamped when you save.
            </p>
          </div>
          <div className="md:col-span-2" data-id="element-5801" data-mp="2wpkmnpxsdvs5">
            <label htmlFor="amend-reason" className="mb-1 block text-sm font-medium text-gray-800" data-id="element-5802" data-mp="2wqm6aisvbqlh">
              Reason for Amendment <span className="text-red-600" aria-hidden="true" data-id="element-5803" data-mp="2nxinrw3d8njz">*</span>
            </label>
            <textarea id="amend-reason" rows={3} value={reason} onChange={e => {
            setReason(e.target.value);
            if (e.target.value.trim()) setReasonError('');
          }} required aria-invalid={Boolean(reasonError)} aria-describedby={reasonError ? 'amend-reason-error' : undefined} placeholder="e.g. Correction of an incorrectly recorded clinical finding" className={`${inputClass} ${reasonError ? 'border-red-400' : 'border-gray-300'} bg-white`} data-id="element-5804" data-mp="2nvpq36aqddo4" />
            {reasonError && <p id="amend-reason-error" className="mt-1 text-sm text-red-700" data-id="element-5805" data-mp="77ve427lwdbb">
                {reasonError}
              </p>}
          </div>
        </div>
        {saveError && <p role="alert" className="mx-5 mb-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" data-id="element-5806" data-mp="1b5p9tss3lmt8">
            <AlertTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-5807" data-mp="126lpklh731d5" />
            {saveError}
          </p>}
        <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-blue-100 px-5 py-3" data-id="element-5808" data-mp="1yvtdu1977okr">
          <p className="text-sm text-gray-600" data-id="element-5809" data-mp="1uvj6ol9o6s4b">
            {changes.length} field{changes.length === 1 ? '' : 's'} corrected
            {addendum.trim() ? ' · supplementary note added' : ''}
          </p>
          <div className="flex gap-2" data-id="element-5810" data-mp="1l2txxy5i7sco">
            <Button type="button" variant="outline" onClick={onBack} disabled={isSaving} data-id="element-5811" data-mp="2iku2rpg2ryc4">
              Cancel
            </Button>
            <Button type="submit" isLoading={isSaving} disabled={isSaving} data-id="element-5812" data-mp="2dsty5jp28h97">
              {!isSaving && <SaveIcon className="mr-1.5 h-4 w-4" aria-hidden="true" data-id="element-5813" data-mp="2l61t2xr5nxpp" />}
              {isSaving ? 'Saving…' : 'Save Amendment'}
            </Button>
          </div>
        </footer>
      </section>
    </form>;
}